// @ts-nocheck
import cloneDeep from 'lodash/cloneDeep';
import { momentTz } from '../dates';

import { matchPath } from 'react-router-dom';

import { isValueEmpty } from '../core';
import { getLabelConfigFile } from '../labelConfigUtils';
import { PATH_TAGS } from '../../const/HelpAndSupport';
import { DEFAULT_ROUTE_CONFIG } from '../../const/defaultRoutesConfig';
import {
  DATE_FORMAT,
  USERS_WITH_DRAFT,
  EMPTY_ARRAY,
  RELATIONS,
} from '../../const/CommonConst';

/**
 * Builds the edit-template route for a template version from the label config
 * path (`templates/edit-template/:tId/:vId`).
 *
 * @param {string|number} templateId - Template id (`:tId`).
 * @param {string|number} versionId - Template version id (`:vId`).
 * @returns {string} Absolute edit-template path, e.g. `/templates/edit-template/<tId>/<vId>`.
 */
export const getEditTemplatePath = (templateId, versionId) =>
  `/${DEFAULT_ROUTE_CONFIG.editTemplate.path
    .replace(':tId', templateId)
    .replace(':vId', versionId)}`;

/**
 * Fetches a specific property (e.g., label, path) from a nested configuration object like LABEL_CONFIG.
 *
 * @param {string} keyPath - The dot-separated key path to the desired object or property (e.g., "MENUS.dashboard").
 * @param {string} [property="label"] - The property to retrieve from the target object (e.g., "label", "path").
 *                                      Defaults to "label" if not specified.
 * @param {*} [fallback=""] - The fallback value to return if the keyPath or the property does not exist.
 *                            Defaults to an empty string.
 * @returns {*} - The value of the specified property if it exists, otherwise the fallback value.
 *
 * @example
 * // Example 1: Fetch a label
 * getLabelPathFromLabelConfig("MENUS.dashboard"); // Output: "Dashboard"
 *
 * @example
 * // Example 2: Fetch a path
 * getLabelPathFromLabelConfig("MENUS.dashboard", "path"); // Output: "/dashboard"
 *
 * @example
 * // Example 3: Handle missing key with a fallback
 * getLabelPathFromLabelConfig("MENUS.unknownKey", "label", "Default Label"); // Output: "Default Label"
 *
 * @example
 * // Example 4: Fetch an entire nested object
 * getLabelPathFromLabelConfig("MENUS.dashboard", "", {}); // Output: { label: "Dashboard", path: "/dashboard" }
 */
export const getLabelPathFromLabelConfig = (
  keyPath,
  property = 'label',
  fallback = ''
) => {
  const keys = keyPath?.split('.');
  const traverse = (config, remainingKeys) => {
    if (!config || remainingKeys?.length === 0) {
      return config;
    }
    const [currentKey, ...restKeys] = remainingKeys;

    // Handle both direct keys and nested `subPaths`
    const nextConfig = config[currentKey] || config?.subPaths?.[currentKey];
    return traverse(nextConfig, restKeys);
  };
  const result = traverse(getLabelConfigFile(), keys || []);
  return result?.[property] ?? fallback;
};

/**
 * Builds the workflow card label for Update Attribute triggers/actions.
 *
 * The base label is the item name, followed by the selected attribute label
 * when available. Relation text is optional because actions show relation
 * context, while triggers only show the attribute name.
 *
 * @param {object} item - Automation trigger/action object.
 * @param {string} item.name - Trigger/action name shown on the workflow card.
 * @param {object} item.parameters - Trigger/action configuration values.
 * @param {string} [item.parameters.attribute_label] - Selected attribute label.
 * @param {string} [item.parameters.relation] - Action relation type.
 * @param {string} [item.parameters.custom_relation] - Custom relation label.
 * @param {object} [options] - Display options.
 * @param {boolean} [options.includeRelation=false] - Append relation text when present.
 * @returns {string} Formatted workflow display label.
 */
export const getUpdateAttributeDisplayLabel = (
  item,
  { includeRelation = false } = {}
) => {
  const { attribute_label, relation, custom_relation } = item?.parameters || {};
  let label = `${item?.name}`;

  if (attribute_label) {
    label += ` - ${attribute_label}`;
  }

  if (includeRelation && relation) {
    if (relation === 'itself') {
      const relationLabel =
        RELATIONS?.find((r) => r?.value === 'itself')?.label || 'Itself';
      label += ` (${relationLabel})`;
    } else if (relation === 'custom_relation' && custom_relation) {
      label += ` (${custom_relation})`;
    }
  }

  return label;
};

/**
 * Formats an entity label into the style "Entity Name (Unique ID)".
 * If both 'entity_name' (or 'entity_alias') and 'unique_id' are present,
 * they are combined. If only one is present, it returns that value.
 *
 * @param {Object} entity - The entity object to format.
 * @param {string} [entity.entity_name] - The name of the entity.
 * @param {string} [entity.entity_alias] - The alias of the entity.
 * @param {string} [entity.unique_id] - The unique identifier of the entity.
 * @returns {string} The formatted label string.
 */
export const formatEntityLabel = (entity) => {
  const name = entity?.entity_name || entity?.entity_alias;
  const uniqueId = entity?.unique_id;

  if (name && uniqueId) {
    return `${name} (${uniqueId})`;
  }
  return name || uniqueId || '';
};

/**
 * @function getTitle
 * @description Format the cell value for the title attribute of the cell.
 * @param {any} value - The cell value.
 * @param {object} attributeInfo - The attribute information.
 * @param {array} usersList - The list of users.
 * @param {array} groupList - The list of groups.
 * @returns {string} - The formatted title string.
 */
export const getTitle = (value, attributeInfo) => {
  if (isValueEmpty(value)) {
    return '';
  }
  const dataType = attributeInfo?.dataType;
  const inputControlType = attributeInfo?.input_control_type;

  let displayValue = value;

  // First check data type
  switch (dataType) {
    case 'Character':
      // For Character data type, check input control type
      switch (inputControlType) {
        case 'MULTI_SELECT':
          if (Array.isArray(value)) {
            displayValue = value.map((v) => v?.value).join(', ');
          }
          break;
        case 'SINGLE_SELECT':
          if (attributeInfo?.id === USERS_WITH_DRAFT) {
            displayValue = value === 'True' ? 'Draft' : '-';
          } else {
            displayValue = value || '-';
          }
          break;
        default:
          displayValue = value.toString();
      }
      break;
    case 'Date': {
      const momentDate = momentTz(value);
      if (momentDate.isValid()) {
        displayValue = momentDate.format(DATE_FORMAT);
      }
      break;
    }
    case 'Association':
      displayValue = value?.unique_id;
      break;
    case 'User':
      // For Character data type, check input control type
      switch (inputControlType) {
        case 'MULTI_SELECT':
          if (Array.isArray(value)) {
            displayValue = value?.map((user) => user)?.join(', ');
          }
          break;
        case 'SINGLE_SELECT':
          displayValue = value;
          break;
      }
      break;
    case 'Group':
      // For Character data type, check input control type
      switch (inputControlType) {
        case 'MULTI_SELECT':
          if (Array.isArray(value)) {
            displayValue = value?.map((group) => group)?.join(', ');
          }
          break;
        case 'SINGLE_SELECT':
          displayValue = value;
          break;
      }
      break;
    default:
      if (dataType === 'MultiSelect' && Array.isArray(value)) {
        displayValue = value.join(', ');
      }
      break;
  }

  return typeof displayValue === 'string' ? displayValue : String(displayValue);
};

/**
 * @function getStatesNameArray
 * @param {array} data
 * @returns {array}
 * @input [{ "id": 1, "name": "ToDo"}, {"id":2,"name": "Development"}]
 * @output ["ToDo", "Development"]
 */
export const getStatesNameArray = (data = EMPTY_ARRAY) => {
  if (isValueEmpty(data)) return [];
  return data?.map((state) => {
    return state?.name;
  });
};

/**
 * Extracts the last meaningful segment from a URL if its length exceeds 50 characters.
 * If the URL is invalid or shorter than 50 characters, returns the original string.
 *
 * @param {string} url - The URL or string to process.
 * @returns {string} - The cleaned last segment of the URL or the original string.
 */
export const getCleanUrlSegment = (url) => {
  let displayText = url;
  try {
    const parsedUrl = new URL(url);
    if (url.length > 50) {
      const pathSegments = parsedUrl.pathname.split('/').filter(Boolean); // Split and filter empty segments
      if (pathSegments.length > 0) {
        const lastSegment = pathSegments[pathSegments.length - 1];
        displayText = decodeURIComponent(lastSegment); // Decode %20 to space
      } else {
        displayText = displayText.slice(0, 50) + '...';
      }
    }
  } catch {
    // Invalid URL; return the original string
  }
  return displayText;
};

/**
 * Retrieves an object based on a matching path.
 * Iterates through the provided object and checks if any object's `path` matches the specified path.
 * Returns an object with the matched keys and their corresponding values.
 *
 * @param {Object} obj - The object to be searched.
 * @param {string} path - The path to match with the object's `path` property.
 * @returns {Object} - An object containing the matched keys and their corresponding values.
 */
const getObjectByPath = (obj, path) => {
  let matchedObj = {};
  Object.keys(obj).forEach((objKey) => {
    if (matchPath(path, `/${obj[objKey]?.path}`)) {
      matchedObj = {
        ...matchedObj,
        [objKey]: {
          ...obj[objKey],
        },
      };
    }
  });

  return matchedObj;
};

/**
 * Retrieves the tags for the current route.
 * If a pathname is provided, the function will return tags specific to that route.
 * If no pathname is provided, it returns all tags for all routes.
 *
 * This function first clones the `PATH_TAGS` object, then updates the paths and tag labels dynamically
 * based on configurations found in the `DEFAULT_ROUTE_CONFIG` and `labelConfig` file.
 * After updating, if a `pathname` is provided, it filters the tags to match the given route path.
 *
 * @param {string | undefined} pathname - The route path to retrieve tags for. If not provided, returns tags for all routes.
 * @returns {Array} - A list of tags for the matching route or all routes if no pathname is provided.
 * Each tag is an object containing `label` and `value` properties.
 * @example
 * const tags = getTagsFromRoute('/models/archived');
 * console.log(tags);
 * // Output: [{ label: 'Archived', value: 'archived' }]
 *
 * @example
 * const allTags = getTagsFromRoute();
 * console.log(allTags);
 * // Output: [{ label: 'Archived', value: 'archived' }, { label: 'Active', value: 'active' }]
 */
export const getTagsFromRoute = (pathname) => {
  let clonedPathsTags = cloneDeep(PATH_TAGS);

  //  We are replacing the default values with dynamic one from label config file for PATH_TAGS
  Object.keys(clonedPathsTags).forEach((key) => {
    clonedPathsTags[key].path = getLabelPathFromLabelConfig(
      DEFAULT_ROUTE_CONFIG?.[key]?.keyPath || ``,
      'path',
      clonedPathsTags[key]?.path
    );

    Object.keys(clonedPathsTags[key]?.tags).forEach((tagKey) => {
      clonedPathsTags[key].tags[tagKey] = getLabelPathFromLabelConfig(
        DEFAULT_ROUTE_CONFIG?.[tagKey]?.keyPath || ``,
        'label',
        clonedPathsTags[key]?.tags[tagKey]
      );
    });
  });

  if (!isValueEmpty(pathname)) {
    clonedPathsTags = getObjectByPath(clonedPathsTags, pathname) || {};
  }
  return Object.values(clonedPathsTags).flatMap(({ tags }) =>
    Object.entries(tags)
      .filter(([, label]) => !isValueEmpty(label))
      .map(([key, label]) => ({
        label,
        value: key,
      }))
  );
};

export const constructBreadcrumbState = (
  _model,
  _breadcrumbDetails,
  includeAssociationTypeBreadcrumb,
  uniqueId,
  dynamicEntityPath,
  type
) => {
  let breadcrumbState = [];

  const entityPath = dynamicEntityPath
    ? `/${dynamicEntityPath}/${uniqueId}`
    : undefined;

  if (includeAssociationTypeBreadcrumb) {
    breadcrumbState = [
      ..._breadcrumbDetails,
      {
        label: uniqueId,
        path: entityPath,
        type,
      },
    ];
  } else {
    const modelIndex = _breadcrumbDetails.findIndex(
      (item) => item?.label === uniqueId
    );

    // We will be removing the trailing models from breadcrumb from the redirected model.
    _breadcrumbDetails.splice(modelIndex);

    breadcrumbState = [
      ..._breadcrumbDetails,
      {
        label: uniqueId,
        path: entityPath,
        type,
      },
    ];
  }

  return breadcrumbState;
};
